import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { buildAuthorizeUrl } from '../../api/authApi';
import type { ProviderId } from '../../api/apiSchemas';
import { useProviders } from '../../auth/authQueries';
import { loginProviders } from '../../auth/providerPresentation';
import { ProviderButton } from '../../components/auth/ProviderButton';
import { Brand, CenteredCardLayout } from '../../components/layout/CenteredCardLayout';
import { Alert, Skeleton } from '../../components/ui';
import { browserNavigation } from '../../lib/browserNavigation';
import { env } from '../../lib/env';
import { parseLoginParams } from '../../lib/queryParams';
import { ROUTES } from '../../lib/routes';
import { track } from '../../lib/logger';

export default function LoginPage() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { client, returnRoute } = useMemo(() => parseLoginParams(search), [search]);
  const providersQuery = useProviders(client);
  const [navigating, setNavigating] = useState(false);

  useEffect(() => {
    track('auth_portal_viewed', { route: '/login', ...(client ? { client } : {}) });
  }, [client]);

  useEffect(() => {
    if (providersQuery.isSuccess) {
      track('auth_provider_registry_loaded', { route: '/login' });
    }
  }, [providersQuery.isSuccess]);

  const providers = providersQuery.data ? loginProviders(providersQuery.data.providers) : [];

  function handleActivate(provider: ProviderId): void {
    if (navigating) return;
    setNavigating(true);
    track('auth_login_started', { provider, ...(client ? { client } : {}) });
    // Dev mock: simulate the provider callback client-side (a real navigation
    // would reload and reset the in-memory mock). Stripped from prod builds.
    // `?mock=new` simulates first-time account creation (-> onboarding).
    if (import.meta.env.DEV && env.useMocks) {
      const kind = search.get('mock') === 'new' ? 'new' : 'existing';
      void import('../../mocks/browser').then(({ simulateProviderLogin }) => {
        simulateProviderLogin(kind);
        navigate(`${ROUTES.authComplete}?provider=${provider}`);
      });
      return;
    }
    browserNavigation.assign(buildAuthorizeUrl(provider, { client, returnRoute }));
  }

  return (
    <CenteredCardLayout>
      <div className="stack">
        <Brand />
        <header>
          <h1>Đăng nhập hoặc tạo tài khoản</h1>
          <p className="muted">Tiếp tục để học tập và sử dụng các dịch vụ của nền tảng.</p>
        </header>

        {navigating ? (
          <Alert tone="info" live>
            Đang chuyển đến trang đăng nhập…
          </Alert>
        ) : null}

        {providersQuery.isLoading ? (
          <div className="stack" aria-hidden="true">
            <Skeleton height="44px" />
            <Skeleton height="44px" />
          </div>
        ) : null}

        {providersQuery.isError ? (
          <Alert tone="error">Không thể tải phương thức đăng nhập. Vui lòng thử lại sau.</Alert>
        ) : null}

        {providersQuery.isSuccess && providers.length === 0 ? (
          <Alert tone="info">
            Hiện chưa có phương thức đăng nhập khả dụng. Vui lòng liên hệ bộ phận hỗ trợ.
          </Alert>
        ) : null}

        {providers.length > 0 ? (
          <div className="stack" role="group" aria-label="Phương thức đăng nhập">
            {providers.map((provider) => (
              <ProviderButton
                key={provider.id}
                provider={provider}
                disabled={navigating}
                onActivate={handleActivate}
              />
            ))}
          </div>
        ) : null}

        <p className="muted" style={{ fontSize: '0.85rem' }}>
          Bằng việc tiếp tục, bạn đồng ý với Điều khoản sử dụng và Chính sách bảo mật.
        </p>
      </div>
    </CenteredCardLayout>
  );
}
